import React, { useEffect, useState } from "react";
import { DownloadIcon, Button as KitButton } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Redirect, useHistory, useParams } from "react-router-dom";

import Back from "~/components/Icons/arrow-prev2";
import Preloader from "~/components/Preloader";
import DocumentsArchiveDropdown from "~/pages/CatalogApplications/CatalogInfa/Parts/DocumentsArchiveDropdown";
import DocumentsSections from "~/pages/CatalogApplications/CatalogInfa/Parts/DocumentsSections";
import InfoSections from "~/pages/CatalogApplications/CatalogInfa/Parts/InfoSections";
import useGetDataCatalogApplicationsId from "~/pages/CatalogApplications/hook/useGetDataCatalogApplicationsId";
import useGetDataDocuments from "~/pages/CatalogApplications/hook/useGetDataDocuments";
import StatusUpdatePopover from "~/pages/CatalogApplications/model/StatusUpdatePopover";
import Button from "~/shared/ui/Fields/Button";
import {
  getCandidatePdf,
  getDocumentFile,
  getPhotoUrl,
  useUpdateCandidateAuditStatus,
  useUpdateCandidateStatus,
} from "~/pages/CatalogApplications/catalogApi";
import { FileAuditUploader } from "~/pages/CatalogApplications/FileUploader";
import {
  getAuditStatusPopoverItems,
  getStatusButtonStyle,
  getStatusPopoverItems,
} from "~/pages/CatalogApplications/model/candidateStatusUi";
import { getCandidateTypeLabel } from "~/pages/CatalogApplications/model/candidateTypeLabels";
import {
  getTerritorialStatus,
  parseDistance,
  TERRITORIAL_STATUS_TEXTS,
} from "~/pages/CatalogApplications/model/territorialStatus";
import { TerritorialStatusIndicator } from "~/pages/CatalogApplications/model/TerritorialStatusIndicator";
import { FormValues } from "~/pages/CatalogApplications/types";
import { usePermissions } from "~/shared/context/PermissionContext";
import { dateFormat } from "~/shared/lib/DateFormat/dateFormat";
import { downloadBlob } from "~/shared/lib/downloadBlob";

import { foundersHelper, TEMPLATE_INDIVIDUAL } from "./options";

import s from "./CatalogInfo.less";

const SECURITY_CHECK_SECTION_TITLE =
  "Перечень для проверки службой безопасности";

const CatalogInfo = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [catalogData, setCatalogData] = useState<FormValues | null>(null);
  const [loading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const goBack = () => {
    history.replace("/catalogApplications");
  };
  const { mutate: updateAuditStatus } = useUpdateCandidateAuditStatus();
  const { mutate: updateStatus } = useUpdateCandidateStatus();
  const { data, error, isLoading } = useGetDataCatalogApplicationsId(id);
  const documentsAllowed = hasPermission("candidateSelection.documents.get");
  const documentsDownloadAllowed = hasPermission(
    "candidateSelection.documents.download",
  );

  const { data: dataDocument, isLoading: isLoadingDocument } =
    useGetDataDocuments({ candidateId: id, enabled: documentsAllowed });

  if (!hasPermission("candidateSelection.candidates.get")) {
    return <Redirect key={`main`} to={"/"} />;
  }

  useEffect(() => {
    if (data) {
      setCatalogData(data);
    }
  }, [data]);

  const distance = catalogData
    ? parseDistance(catalogData.distanceToNearestDealer)
    : 0;

  const createdYear = catalogData?.createdAt
    ? new Date(catalogData.createdAt).getFullYear()
    : null;
  const lastYear = createdYear !== null ? createdYear - 2 : "-";
  const currentYear = createdYear !== null ? createdYear - 1 : "-";
  const isDistanceToNearestDealer = catalogData && distance < 4.5;

  if (loading || isLoading || isLoadingDocument) {
    return <Preloader />;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  if (!catalogData) {
    return <div className={s.noData}>{t("common.no_data")}</div>;
  }

  const fileIdMap = new Map(
    (data?.documents || []).map((doc) => [doc.type, doc.fileId]),
  );

  const documentSections = (dataDocument || []).map((section) => ({
    title: section.name,
    sectionData: section.documents
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((doc, docIndex) => ({
        label: `${docIndex + 1}. ${doc.name}`,
        value: fileIdMap.get(doc.id) || null,
        type: doc.id,
      })),
  }));

  const handleDownloadAudit = async (fileId: string, label: string) => {
    try {
      const response = await getDocumentFile(fileId);

      const contentDisposition = response.headers.get("content-disposition");
      let filename = "";

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition,
        );
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      if (!filename) {
        const contentType = response.headers.get("content-type") || "";
        const extension = contentType.split("/").pop() || "";
        const safeLabel = label.replace(/[\\/:*?"<>|]/g, "_");
        filename = `${safeLabel}.${extension}`;
      }

      await downloadBlob(response, filename);
    } catch (err) {
      console.error("Ошибка при скачивании файла:", err);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsPdfLoading(true);
      const response = await getCandidatePdf(catalogData.id);

      const contentDisposition = response.headers.get("content-disposition");
      let filename = "заявка.pdf";

      if (contentDisposition) {
        const encoded = /filename\*=([^;]*)/i.exec(contentDisposition);
        if (encoded && encoded[1]) {
          const value = encoded[1].trim().replace(/['"]/g, "");
          try {
            filename = decodeURIComponent(value.replace(/^UTF-8''/i, ""));
          } catch {
            filename = value;
          }
        } else {
          const matches = /filename=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition,
          );
          if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
      }

      await downloadBlob(response, filename);
    } catch (err) {
      console.error("Ошибка при выгрузке заявки PDF-файлом:", err);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const territorialStatus = getTerritorialStatus(
    catalogData.distanceToNearestDealer,
    catalogData.candidateType,
    catalogData.isHolding,
  );

  const auditUploader = (
    <FileAuditUploader
      candidateId={catalogData.id}
      isCandidateFile={!!catalogData.auditFileId}
      onUploaded={() =>
        queryClient.invalidateQueries(["getCatalogData", catalogData.id])
      }
    />
  );

  return (
    <div className={s.wrapper_indicators}>
      <div className={s.title}>
        <div>{t(`catalogApplications.info_title`)}</div>
        <Button
          txtBtn={"Назад"}
          onClick={goBack}
          small
          customClassName={s.add_bth}
        >
          <Back />
        </Button>
      </div>
      <div className={s.wrapper}>
        <div className={s.wrapperBlock}>
          {typeof catalogData.isCompetitive === "boolean" && (
            <div className={s.competitiveMark}>
              {t(
                catalogData.isCompetitive
                  ? "catalog_applications.competitiveApplication"
                  : "catalog_applications.notCompetitiveApplication",
              )}
            </div>
          )}
          <div className={s.wrapperVariant}>
            {t("catalog_applications.candidateType")}:{" "}
            {getCandidateTypeLabel(t, catalogData.candidateType)}
          </div>
          <div className={s.wrapperDistance}>
            {territorialStatus ? (
              <TerritorialStatusIndicator status={territorialStatus} />
            ) : null}
            <span>
              –{" "}
              {territorialStatus
                ? TERRITORIAL_STATUS_TEXTS[territorialStatus]
                : null}
            </span>
          </div>
        </div>
        <div className={s.wrapperActions}>
          <KitButton
            bold
            color="secondary"
            size="medium"
            loading={isPdfLoading}
            icon={<DownloadIcon size={16} fill="#fff" viewBox="0 0 16 16" />}
            iconPosition="end"
            onClick={handleDownloadPdf}
          >
            Выгрузить заявку PDF-файлом
          </KitButton>
          <div className={s.wrapperStatus} onClick={(e) => e.stopPropagation()}>
            <div className={s.statusBadgeWrap}>
              <div
                className={s.statusBadge}
                style={getStatusButtonStyle(catalogData.status)}
              >
                {catalogData.status}
              </div>
            </div>

            <StatusUpdatePopover
              allowed={hasPermission("candidateSelection.status.update")}
              items={getStatusPopoverItems()}
              direction="left"
              selectedValue={catalogData.status}
              onSelect={(value) => {
                updateStatus({
                  status: value,
                  candidateId: catalogData.id,
                });
                // refetch();
              }}
              triggerClassName={s.changeStatus}
            />
          </div>
        </div>
      </div>
      {/*<div className={s.btn}>*/}

      {/*</div>*/}
      <div className={s.catalogItem}>
        <InfoSections
          accord
          title={"Основная информация"}
          sectionData={[
            {
              label: "Наименование юридического лица",
              value: catalogData.legalPersonName,
            },
            { label: "ИНН", value: catalogData.inn },
            { label: "ОГРН", value: catalogData.ogrn },
            { label: "КПП", value: catalogData.kpp },
            {
              label: "Уставной капитал",
              value: catalogData.foundingCorporationCapital,
            },
            {
              label: "Торговое наименование (бренд)",
              value: catalogData.tradingName,
            },
            {
              label: "Адрес будущего дилерского центра",
              value: catalogData.futureAddress,
            },
          ]}
        />

        <InfoSections
          accord
          title={"Контактная информация"}
          sectionData={[
            {
              label: "ФИО руководителя (полностью)",
              value: catalogData.executiveFullName,
            },
            {
              label: "Должность руководителя",
              value: catalogData.executivePosition,
            },
            {
              label: "Телефон руководителя (сотовый)",
              value: catalogData.executivePersonalPhone,
            },
            {
              label: "Телефон руководителя (рабочий)",
              value: catalogData.executiveWorkPhone,
            },
            {
              label: "Email руководителя",
              value: catalogData.executiveEmail,
            },
            {
              label: "Ф.И.О. главного бухгалтера (полностью)",
              value: catalogData.chiefAccountantFullName,
            },
            {
              label: "Телефон главного бухгалтера (рабочий)",
              value: catalogData.chiefAccountantWorkPhone,
            },
            {
              label: "Телефон главного бухгалтера (сотовый)",
              value: catalogData.chiefAccountantPersonalPhone,
            },
            {
              label: "E-mail главного бухгалтера",
              value: catalogData.chiefAccountantEmail,
            },
          ]}
        />

        <InfoSections
          accord
          title={"Общая информация о компании"}
          sectionData={[
            {
              label: "Телефон",
              value: catalogData.phone,
            },
            {
              label: "E-mail",
              value: catalogData.email,
            },
            {
              label: "Веб-Сайт",
              value: catalogData.website,
            },
            {
              label: "Координаты дилерского центра",
              value: `${catalogData.coordinates.x}, ${catalogData.coordinates.y}`,
            },
          ]}
        />

        {!isDistanceToNearestDealer && (
          <>
            <InfoSections
              accord
              title={"Генеральный директор"}
              sectionData={[
                {
                  label: "Ф.И.О. (полностью)",
                  value: catalogData.generalManagerFullName,
                },
                {
                  label: "Дата рождения",
                  value: catalogData.generalManagerBirthday
                    ? dateFormat(catalogData.generalManagerBirthday, {
                        type: "date",
                      })
                    : "",
                },
                {
                  label: "Образование",
                  value: catalogData.generalManagerEducation,
                },
                {
                  label: "Предыдущее место работы",
                  value: catalogData.generalManagerLastJob,
                },
                {
                  label: "Телефон",
                  value: catalogData.generalManagerPhone,
                },
                {
                  label: "E-mail",
                  value: catalogData.generalManagerEmail,
                },
              ]}
            />

            <InfoSections
              accord
              title={
                "Руководитель проекта LADA\n" +
                "(ответственный за реализацию проекта)"
              }
              sectionData={[
                {
                  label: "Ф.И.О. (полностью)",
                  value: catalogData.projectManagerFullName,
                },
                {
                  label: "Дата рождения",
                  value: catalogData.projectManagerBirthday
                    ? dateFormat(catalogData.projectManagerBirthday, {
                        type: "date",
                      })
                    : "",
                },
                {
                  label: "Образование",
                  value: catalogData.projectManagerEducation,
                },
                {
                  label: "Предыдущее место работы",
                  value: catalogData.projectManagerLastJob,
                },
                {
                  label: "Телефон",
                  value: catalogData.projectManagerPhone,
                },
                {
                  label: "E-mail",
                  value: catalogData.projectManagerEmail,
                },
              ]}
            />

            {catalogData?.founders?.map((founder, index) => (
              <InfoSections
                key={founder.id}
                title={"Информация об учредителях"}
                subTitle={`Учредитель №${index + 1}`}
                accord
                sectionData={foundersHelper(founder)}
              />
            ))}

            {catalogData?.existingDealers?.map((dealer, index) => {
              const dealerPhotos = [
                dealer.leftPhotoId ? getPhotoUrl(dealer.leftPhotoId) : null,
                dealer.rightPhotoId ? getPhotoUrl(dealer.rightPhotoId) : null,
                dealer.showroomPhotoId
                  ? getPhotoUrl(dealer.showroomPhotoId)
                  : null,
                dealer.servicePhotoId
                  ? getPhotoUrl(dealer.servicePhotoId)
                  : null,
                dealer.additionalPhotoId
                  ? getPhotoUrl(dealer.additionalPhotoId)
                  : null,
              ].filter(Boolean) as string[];

              return (
                <InfoSections
                  accord
                  key={index}
                  title={"Описание существующего бизнеса"}
                  subTitle={`Дилерский центр №${index + 1}:`}
                  sectionData={[
                    {
                      label: "Начало деятельности",
                      value: dealer.activeFrom
                        ? dateFormat(dealer.activeFrom, { type: "date" })
                        : "",
                    },
                    {
                      label: "Площадь сервиса,㎡",
                      value: dealer.serviceArea,
                    },
                    {
                      label: "Площадь шоурума,㎡",
                      value: dealer.showroomArea,
                    },
                    {
                      label: "Тип дилерского центра",
                      value: dealer.type,
                    },
                    {
                      label: "Адрес дилерского центра",
                      value: dealer.address,
                    },
                    {
                      label: `Продажи новых ам, шт ${lastYear}`,
                      value: dealer.salesLastYear,
                    },
                    {
                      label: `Продажи новых ам, шт ${currentYear}`,
                      value: dealer.salesCurrentYear,
                    },
                    {
                      label: `Оборот ${currentYear} (руб)`,
                      value: dealer.turnoverCurrentYear,
                    },
                    {
                      label: `Чистая прибыль ${currentYear} (руб)`,
                      value: dealer.pureNetProfitCurrentYear,
                    },
                    {
                      label: `EBITDA ${currentYear}`,
                      value: dealer.ebitdaCurrentYear,
                    },
                    {
                      label: "Количество сотрудников",
                      value: dealer.employeesCount,
                    },
                    {
                      label: "Наличие инвестиционных обязательств",
                      value: dealer.hasInvestmentObligations ? "Да" : "Нет",
                    },
                    ...(dealerPhotos.length
                      ? [
                          {
                            label: "Прикрепление фотографий (JPEG)",
                            value: dealerPhotos,
                          },
                        ]
                      : []),
                  ]}
                />
              );
            })}

            <InfoSections
              accord
              title={"Информация о предложении"}
              sectionData={[
                {
                  label: "Формат инвестиций",
                  value: catalogData.formatOfInvestments,
                },
                {
                  label: "Тип дилерского центра",
                  value: catalogData.type,
                },
                {
                  label: "Статус собственности",
                  value: catalogData.ownershipStatus,
                },
                {
                  label: "Первая линия",
                  value: catalogData.isFirstLane ? "Да" : "Нет",
                },
                {
                  label: "Размер земельного участка,㎡",
                  value: catalogData.landArea,
                },
                {
                  label: "Площадь шоурума,㎡",
                  value: catalogData.showroomArea,
                },
                {
                  label: "Площадь слесарного цеха,㎡",
                  value: catalogData.metalworkingArea,
                },
                {
                  label: "Площадь склада,㎡",
                  value: catalogData.warehouseArea,
                },
                {
                  label: "Кузовной цех,㎡",
                  value: catalogData.carBodyWorkingArea,
                },
                {
                  label: "Кузовной цех",
                  value: catalogData.carBodyWorking,
                },
                {
                  label: "Срок готовности к запуску центра, ММ/ГГГГ",
                  value: `${catalogData.readyMonth}/${catalogData.readyYear}`,
                },
              ]}
            />

            <InfoSections
              accord
              title={"Информация о регионе"}
              sectionData={[
                {
                  label: "Численность региона",
                  value: catalogData.regionPopulation
                    ? String(catalogData.regionPopulation)
                    : "",
                },
                {
                  label: "Численность города",
                  value: catalogData.cityPopulation
                    ? String(catalogData.cityPopulation)
                    : "",
                },
                {
                  label: "Социально-экономическая характеристика региона",
                  value: catalogData.regionCharacteristics,
                },
                {
                  label: "Средний уровень заработной платы в городе",
                  value: catalogData.averageCitySalary,
                },
              ]}
            />

            <InfoSections
              accord
              title={"Финансовые критерии"}
              sectionData={[
                {
                  label: "Уставный капитал",
                  value: catalogData.foundingCapital,
                },
                {
                  label: "Собственные средства",
                  value: catalogData.ownCapital,
                },
                {
                  label: "Оборотный капитал к выручке с НДС (в днях)",
                  value: catalogData.workingRevenueCapital,
                },
                {
                  label: "Долгосрочные обязательства/Собственные средства",
                  value: catalogData.longTermLiabilities,
                },
              ]}
            />

            <InfoSections
              accord
              title={"Прикрепление фотографий (JPEG, PNG)"}
              sectionData={[
                {
                  label:
                    "Карта города (также отметьте расположение других брендов)",
                  value: catalogData.cityMapPhotoId
                    ? getPhotoUrl(catalogData.cityMapPhotoId)
                    : "",
                },
                {
                  label: "Генеральный план",
                  value: catalogData.generalPlanPhotoId
                    ? getPhotoUrl(catalogData.generalPlanPhotoId)
                    : "",
                },
                {
                  label: "План первого этажа",
                  value: catalogData.firstFloorPlanPhotoId
                    ? getPhotoUrl(catalogData.firstFloorPlanPhotoId)
                    : "",
                },
                {
                  label: "План второго этажа",
                  value: catalogData.secondFloorPlanPhotoId
                    ? getPhotoUrl(catalogData.secondFloorPlanPhotoId)
                    : "",
                },
                {
                  label: "Разрез здания",
                  value: catalogData.buildingSectionPhotoId
                    ? getPhotoUrl(catalogData.buildingSectionPhotoId)
                    : "",
                },
                {
                  label:
                    "Фото з/у или здания. Вид на фасад с проезжей части слева",
                  value: catalogData.leftPhotoId
                    ? getPhotoUrl(catalogData.leftPhotoId)
                    : "",
                },
                {
                  label:
                    "Фото з/у или здания. Вид на фасад с проезжей части справа",
                  value: catalogData.rightPhotoId
                    ? getPhotoUrl(catalogData.rightPhotoId)
                    : "",
                },
                {
                  label: "Фото шоу-рума (общий вид)",
                  value: catalogData.showroomPhotoId
                    ? getPhotoUrl(catalogData.showroomPhotoId)
                    : "",
                },
                {
                  label: "Фото сервисной зоны (общий вид)",
                  value: catalogData.servicePhotoId
                    ? getPhotoUrl(catalogData.servicePhotoId)
                    : "",
                },
                {
                  label: "Дополнительные фото",
                  value: catalogData.additionalPhotoId
                    ? getPhotoUrl(catalogData.additionalPhotoId)
                    : "",
                },
              ]}
            />
            {documentsAllowed &&
              documentSections.map((section, index) => (
                <DocumentsSections
                  key={index}
                  title={section.title}
                  sectionData={section.sectionData}
                  documents={catalogData.documents}
                  accord
                  titleAddon={
                    documentsDownloadAllowed &&
                    section.title === SECURITY_CHECK_SECTION_TITLE ? (
                      <DocumentsArchiveDropdown
                        candidateId={catalogData.id}
                        legalPersonName={catalogData.legalPersonName}
                        sections={(dataDocument || []).map((section) => ({
                          id: section.id,
                          name: section.name,
                        }))}
                      />
                    ) : undefined
                  }
                />
              ))}
            <div className={s.wrapperBlock}>
              <div className={s.wrapperAudit}>
                <div className={s.titleAudit}>Аудит</div>

                <div onClick={(e) => e.stopPropagation()}>
                  <div className={s.statusBadgeWrap}>
                    <div
                      className={s.statusBadge}
                      style={getStatusButtonStyle(catalogData.auditStatus)}
                    >
                      {catalogData.auditStatus}
                    </div>
                  </div>

                  <StatusUpdatePopover
                    allowed={hasPermission(
                      "candidateSelection.audit.status.update",
                    )}
                    items={getAuditStatusPopoverItems()}
                    direction="right"
                    selectedValue={catalogData.auditStatus}
                    onSelect={(value) => {
                      updateAuditStatus({
                        auditStatus: value,
                        candidateId: catalogData.id,
                      });
                      // refetch();
                    }}
                    triggerClassName={s.changeStatus}
                  />
                </div>
              </div>
              <div className={s.textBottom}>
                <div>
                  {catalogData.auditFileId ? (
                    <div>
                      <div className={s.file}>
                        Файл есть &nbsp;
                        <KitButton
                          size="small"
                          onClick={() =>
                            handleDownloadAudit(
                              catalogData.auditFileId,
                              "Файл аудита",
                            )
                          }
                        >
                          Скачать
                        </KitButton>
                      </div>

                      {hasPermission("candidateSelection.audit.upload") &&
                        auditUploader}
                    </div>
                  ) : hasPermission("candidateSelection.audit.upload") ? (
                    auditUploader
                  ) : (
                    <div>Файл ещё не загружен</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CatalogInfo;
